// src/features/datev/components/EauUebersicht.tsx
//
// Eine Liste aller eAU-Anfragen. Sie ersetzt die frühere Aufteilung in
// Nachweisstand und Vorgangsliste, die dieselben Fälle zweimal zeigte, einmal
// nach Zeitraum und einmal nach Einzelanfrage, ohne dass der Unterschied
// erkennbar war.
//
// Die Liste ist zum Ansehen da, nicht zum Abarbeiten. Deshalb keine Gruppen,
// keine Reiter, keine Aktionen. Der Zustand steht in der Zeile.
//
// Was eine Zeile ist:
//   Zeitraum   Ein angefragter Krankheitszeitraum. Mehrere Anfragen dazu
//              (Erstanfrage plus Nachfassanfragen) bilden eine Zeile, denn
//              fachlich geht es um den Zeitraum, nicht um die Einzelanfrage.
//   Vorgang    Eine Anfrage ohne hinterlegten Zeitraum. Nur Altbestand aus
//              der Zeit, in der bei manuellen Anfragen kein Ende erfasst
//              wurde. Ohne Sollzeitraum ist keine Deckungsprüfung möglich.
//
// Der Zeitstrahl zeigt, welcher Teil des Zeitraums von der Kasse bestätigt
// ist. Der eAU-Anforderungsdatensatz kennt kein Enddatum, die Kasse antwortet
// deshalb immer nur mit dem AU-Zeitraum rund um das angefragte Startdatum.
// Eine Textangabe wie "bestätigt bis 24.07." verschweigt, dass dafür mehrere
// Anfragen nötig waren und eine weitere folgt.
//
// Alle Werte kommen aus datev_eau_uebersicht_v und werden dort live berechnet.
// Läge die Berechnung im nächtlich fortgeschriebenen Feld, zeigte die Liste
// nach einer eingetroffenen Rückmeldung bis zu drei Tage lang eine Lücke, die
// es nicht mehr gibt.
//
// Mit customerId zeigt sie einen Mandanten, ohne customerId alle.

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Inbox } from 'lucide-react';
import { supabase } from '../../../shared/services/supabase';

interface EauUebersichtProps {
  /** Ohne Angabe werden alle Mandanten gezeigt. */
  customerId?: string;
  /** Nur zur Anzeige im Kopf, wenn ein einzelner Mandant gefiltert ist. */
  clientId?: string;
}

interface Abschnitt {
  von: string | null;
  bis: string | null;
}

interface UebersichtZeile {
  art: 'zeitraum' | 'vorgang';
  id: string;
  customer_id: string;
  client_id: string;
  mandant: string | null;
  personnel_number: string;
  beginn: string | null;
  ende: string | null;
  dauer_tage: number | null;
  gedeckt_bis: string | null;
  bestaetigte_abschnitte: Abschnitt[];
  tage_ohne_nachweis: number | null;
  nachweis_status: string;
  vorgaenge: number;
  nachfass_anzahl: number;
  ablehnungstext: string | null;
  zuletzt_am: string | null;
}

/** Wie viele Zeilen höchstens geladen werden. */
const ZEILEN_LIMIT = 200;

const STATUS_TEXT: Record<string, string> = {
  vollstaendig_gedeckt:         'Vollständig bestätigt',
  wartet_auf_antwort:           'Antwort steht aus',
  nachweis_unvollstaendig:      'Nachweis unvollständig',
  kein_elektronischer_nachweis: 'Kein elektronischer Nachweis',
  stammdaten_unvollstaendig:    'Stammdaten unvollständig',
  nachfassen_erschoepft:        'Nachfassen erschöpft',
  kein_zeitraum_hinterlegt:     'Kein Zeitraum hinterlegt',
  storniert:                    'Storniert',
  ignoriert:                    'Ignoriert'
};

/** bg und fg für das Kennzeichen, balken für den offenen Teil im Zeitstrahl. */
const STATUS_FARBE: Record<string, { bg: string; fg: string; balken: string }> = {
  vollstaendig_gedeckt:         { bg: '#D1FAE5', fg: '#065F46', balken: '#9FE1CB' },
  wartet_auf_antwort:           { bg: '#DBEAFE', fg: '#1E40AF', balken: '#B5D4F4' },
  nachweis_unvollstaendig:      { bg: '#FEF3C7', fg: '#92400E', balken: '#FAC775' },
  kein_elektronischer_nachweis: { bg: '#FAECE7', fg: '#993C1D', balken: '#F0997B' },
  stammdaten_unvollstaendig:    { bg: '#FEE2E2', fg: '#991B1B', balken: '#F09595' },
  nachfassen_erschoepft:        { bg: '#FEE2E2', fg: '#991B1B', balken: '#F09595' },
  kein_zeitraum_hinterlegt:     { bg: '#F3F4F6', fg: '#4B5563', balken: '#D3D1C7' },
  storniert:                    { bg: '#F3F4F6', fg: '#4B5563', balken: '#D3D1C7' },
  ignoriert:                    { bg: '#F3F4F6', fg: '#4B5563', balken: '#D3D1C7' }
};

/**
 * Erläuterung zum Zustand. Steht nur dort, wo der Zustand ohne sie
 * missverstanden würde.
 */
const STATUS_ERLAEUTERUNG: Record<string, string> = {
  kein_elektronischer_nachweis:
    'Der Krankenkasse liegt für diesen Zeitraum keine elektronische Bescheinigung vor. Das besagt nicht, '
    + 'dass keine Arbeitsunfähigkeit bestand, sondern nur, dass die Kasse dazu keinen Beleg hat, etwa wenn '
    + 'kein Arzt aufgesucht wurde. Bei Zweifeln den Mitarbeiter um einen geeigneten Nachweis bitten.',
  stammdaten_unvollstaendig:
    'Die hinterlegten Angaben reichen für eine Anfrage nicht aus, meist ist die eingetragene Krankenkasse '
    + 'nicht zuständig. Bitte die richtige Krankenkasse in Erfahrung bringen und in den Stammdaten hinterlegen.',
  kein_zeitraum_hinterlegt:
    'Zu dieser Anfrage wurde kein Krankheitszeitraum erfasst. Sie stammt aus der Zeit vor Einführung des '
    + 'Enddatums, eine Prüfung auf Vollständigkeit ist daher nicht möglich.',
  nachfassen_erschoepft:
    'Die zulässige Anzahl an Folgeanfragen ist ausgeschöpft, ohne dass der Nachweis vollständig wurde.'
};

const BESTAETIGT_FARBE = '#9FE1CB';

function formatKurzDatum(iso: string | null): string {
  if (!iso) return '—';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

function formatZeitpunkt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });
}

/** Kalendarische Tagesdifferenz, in UTC gerechnet. */
function tageZwischen(vonIso: string, bisIso: string): number {
  const von = new Date(`${vonIso}T00:00:00Z`).getTime();
  const bis = new Date(`${bisIso}T00:00:00Z`).getTime();
  return Math.round((bis - von) / 86400000);
}

function naechsterTag(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().substring(0, 10);
}

/**
 * Baut die Segmente des Zeitstrahls. Bestätigte Abschnitte werden auf den
 * erfassten Zeitraum beschnitten, damit ein Abschnitt, der davor beginnt oder
 * danach endet, den Balken nicht sprengt. Zwischen zwei aufeinanderfolgenden
 * Abschnitten bleibt eine feine Lücke, damit erkennbar ist, dass es zwei
 * Bescheinigungen waren und nicht eine durchgehende.
 */
function baueSegmente(
  beginn: string,
  ende: string,
  abschnitte: Abschnitt[],
  offenFarbe: string
): Array<{ anteil: number; farbe: string }> {
  const gesamt = tageZwischen(beginn, ende) + 1;
  if (gesamt <= 0) return [{ anteil: 100, farbe: offenFarbe }];

  const bereiche = (abschnitte || [])
    .filter(a => a.von && a.bis)
    .map(a => ({
      von: (a.von as string) < beginn ? beginn : (a.von as string),
      bis: (a.bis as string) > ende ? ende : (a.bis as string)
    }))
    .filter(a => a.bis >= a.von)
    .sort((x, y) => (x.von < y.von ? -1 : x.von > y.von ? 1 : 0));

  const segmente: Array<{ anteil: number; farbe: string }> = [];
  let cursor = beginn;

  for (const b of bereiche) {
    if (b.bis < cursor) continue;
    if (b.von > cursor) {
      const luecke = tageZwischen(cursor, b.von);
      if (luecke > 0) segmente.push({ anteil: (luecke / gesamt) * 100, farbe: offenFarbe });
      cursor = b.von;
    }
    const laenge = tageZwischen(cursor, b.bis) + 1;
    if (laenge > 0) {
      if (segmente.length > 0 && segmente[segmente.length - 1].farbe === BESTAETIGT_FARBE) {
        segmente.push({ anteil: 0.6, farbe: 'transparent' });
      }
      segmente.push({ anteil: (laenge / gesamt) * 100, farbe: BESTAETIGT_FARBE });
    }
    cursor = naechsterTag(b.bis);
  }

  if (cursor <= ende) {
    const rest = tageZwischen(cursor, ende) + 1;
    if (rest > 0) segmente.push({ anteil: (rest / gesamt) * 100, farbe: offenFarbe });
  }

  return segmente.length > 0 ? segmente : [{ anteil: 100, farbe: offenFarbe }];
}

export const EauUebersicht: React.FC<EauUebersichtProps> = ({ customerId, clientId }) => {
  const [zeilen, setZeilen] = useState<UebersichtZeile[]>([]);
  const [namen, setNamen] = useState<Map<string, string>>(new Map());
  const [gesamt, setGesamt] = useState<number>(0);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      let abfrage = supabase
        .from('datev_eau_uebersicht_v')
        .select('art, id, customer_id, client_id, mandant, personnel_number, beginn, ende, dauer_tage, gedeckt_bis, bestaetigte_abschnitte, tage_ohne_nachweis, nachweis_status, vorgaenge, nachfass_anzahl, ablehnungstext, zuletzt_am', { count: 'exact' })
        .order('zuletzt_am', { ascending: false, nullsFirst: false })
        .limit(ZEILEN_LIMIT);

      if (customerId) abfrage = abfrage.eq('customer_id', customerId);

      const { data, error, count } = await abfrage;
      if (error) throw new Error(error.message);

      const liste = (data as unknown as UebersichtZeile[]) || [];
      setZeilen(liste);
      setGesamt(count ?? liste.length);

      // Namen nur für die angezeigten Personalnummern nachladen. Über alle
      // Mandanten wäre ein Vollabzug des Spiegels teurer als die Liste selbst.
      if (liste.length > 0) {
        const nummern = Array.from(new Set(liste.map(z => z.personnel_number)));
        const kunden = Array.from(new Set(liste.map(z => z.customer_id)));
        const { data: mitarbeiter } = await supabase
          .from('datev_employees')
          .select('customer_id, personalnummer, vorname, nachname')
          .in('customer_id', kunden)
          .in('personalnummer', nummern)
          .limit(100000);

        const karte = new Map<string, string>();
        for (const m of ((mitarbeiter ?? []) as Array<Record<string, string | null>>)) {
          const name = [m.vorname, m.nachname].filter(Boolean).join(' ').trim();
          if (name) karte.set(`${m.customer_id}|${m.personalnummer}`, name);
        }
        setNamen(karte);
      } else {
        setNamen(new Map());
      }
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
      setZeilen([]);
    } finally {
      setLaedt(false);
    }
  }, [customerId]);

  useEffect(() => { laden(); }, [laden]);

  const offeneTage = zeilen.reduce((summe, z) => summe + (z.tage_ohne_nachweis || 0), 0);

  return (
    <div style={{
      backgroundColor: 'white', border: '1px solid #E5E7EB',
      borderRadius: '8px', padding: '16px 20px'
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: '14px'
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '15px', color: '#111827' }}>
            eAU-Anfragen ({gesamt})
          </h3>
          <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
            {customerId
              ? `Angefragte Krankheitszeiträume dieses Mandanten${clientId ? ` (${clientId})` : ''}`
              : 'Angefragte Krankheitszeiträume aller Mandanten'}
            {offeneTage > 0 && ` · ${offeneTage} Tage ohne Nachweis`}
            {gesamt > zeilen.length && ` · die ${zeilen.length} jüngsten werden gezeigt`}
          </div>
        </div>
        <button
          onClick={laden}
          disabled={laedt}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '6px 10px',
            backgroundColor: 'white', border: '1px solid #D1D5DB',
            borderRadius: '4px', cursor: laedt ? 'not-allowed' : 'pointer',
            fontSize: '12px', color: '#374151'
          }}
        >
          <RefreshCw size={12} />
          {laedt ? 'Wird aktualisiert...' : 'Liste aktualisieren'}
        </button>
      </div>

      {fehler && (
        <div style={{
          backgroundColor: '#FEE2E2', border: '1px solid #FECACA',
          borderRadius: '6px', padding: '10px 12px',
          fontSize: '12px', color: '#991B1B', marginBottom: '12px'
        }}>
          Die Liste konnte nicht geladen werden: {fehler}
        </div>
      )}

      {!laedt && !fehler && zeilen.length === 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          fontSize: '13px', color: '#6B7280', padding: '24px'
        }}>
          <Inbox size={16} />
          Noch keine eAU-Anfragen vorhanden.
        </div>
      )}

      {zeilen.map((z, index) => {
        const farbe = STATUS_FARBE[z.nachweis_status] || STATUS_FARBE.kein_zeitraum_hinterlegt;
        const name = namen.get(`${z.customer_id}|${z.personnel_number}`);
        const erlaeuterung = STATUS_ERLAEUTERUNG[z.nachweis_status];
        const hatZeitraum = !!(z.beginn && z.ende);
        const segmente = hatZeitraum
          ? baueSegmente(z.beginn as string, z.ende as string, z.bestaetigte_abschnitte, farbe.balken)
          : [];
        const anzahlBestaetigt = (z.bestaetigte_abschnitte || []).filter(a => a.von && a.bis).length;

        return (
          <div
            key={`${z.art}-${z.id}`}
            style={{
              paddingTop: index === 0 ? 0 : '12px',
              marginTop: index === 0 ? 0 : '12px',
              borderTop: index === 0 ? 'none' : '1px solid #F3F4F6'
            }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'baseline', gap: '12px', marginBottom: '3px'
            }}>
              <span style={{ fontSize: '13px', fontWeight: 500, color: '#111827' }}>
                {name || 'Name nicht im Spiegel'}
                <span style={{ fontSize: '11px', fontWeight: 400, color: '#9CA3AF', marginLeft: '6px' }}>
                  Personalnummer {z.personnel_number}
                </span>
              </span>
              <span style={{
                flexShrink: 0,
                backgroundColor: farbe.bg, color: farbe.fg,
                padding: '2px 8px', borderRadius: '10px',
                fontSize: '11px', fontWeight: 500, whiteSpace: 'nowrap'
              }}>
                {STATUS_TEXT[z.nachweis_status] || z.nachweis_status}
              </span>
            </div>

            <div style={{
              display: 'flex', justifyContent: 'space-between',
              gap: '12px', fontSize: '11px', color: '#6B7280', marginBottom: '6px'
            }}>
              <span>
                {hatZeitraum
                  ? `${formatKurzDatum(z.beginn)} bis ${formatKurzDatum(z.ende)} · ${z.dauer_tage} ${z.dauer_tage === 1 ? 'Tag' : 'Tage'}`
                  : `Angefragt ab ${formatKurzDatum(z.beginn)}`}
                {z.vorgaenge > 1 && ` · ${z.vorgaenge} Anfragen`}
              </span>
              <span style={{ flexShrink: 0 }}>
                {!customerId && (z.mandant || z.client_id)}
                {!customerId && ' · '}
                {formatZeitpunkt(z.zuletzt_am)}
              </span>
            </div>

            {hatZeitraum && (
              <>
                <div style={{ display: 'flex', height: '16px', borderRadius: '3px', overflow: 'hidden' }}>
                  {segmente.map((s, i) => (
                    <div key={i} style={{ width: `${s.anteil}%`, backgroundColor: s.farbe }} />
                  ))}
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: '11px', marginTop: '4px'
                }}>
                  <span style={{ color: '#6B7280' }}>
                    {z.gedeckt_bis
                      ? `Bestätigt bis ${formatKurzDatum(z.gedeckt_bis)}`
                      : 'Kein bestätigter Abschnitt'}
                    {anzahlBestaetigt > 1 && ` · ${anzahlBestaetigt} Bescheinigungen`}
                  </span>
                  {(z.tage_ohne_nachweis || 0) > 0 && (
                    <span style={{ color: farbe.fg, fontWeight: 500 }}>
                      {z.tage_ohne_nachweis} {z.tage_ohne_nachweis === 1 ? 'Tag' : 'Tage'} ohne Nachweis
                    </span>
                  )}
                </div>
              </>
            )}

            {erlaeuterung && (
              <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '6px', lineHeight: 1.5 }}>
                {erlaeuterung}
                {z.ablehnungstext && ` (${z.ablehnungstext})`}
              </div>
            )}

            {z.nachweis_status === 'nachweis_unvollstaendig' && (
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
                Folgeanfrage geht im nächsten Nachtlauf raus · bisher {z.nachfass_anzahl} von 10
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
